import { Head, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Plus, Clock, BookOpen, HelpCircle, Video, Star } from 'lucide-react'

interface TrainingVideo {
  id: number
  title: string
  description: string
  video_url: string
  duration_minutes: number
  category: string
  sort_order: number
  is_required: boolean
  module_type: 'video' | 'text' | 'quiz'
}

interface Props extends PageProps {
  videos: TrainingVideo[]
}

const moduleTypeIcons = {
  video: Video,
  text: BookOpen,
  quiz: HelpCircle
}

const moduleTypeLabels = {
  video: 'Video',
  text: 'Reading',
  quiz: 'Quiz'
}

export default function TrainingIndex({ auth, videos }: Props) {
  const isAdmin = auth.user && ['administrator', 'owner', 'staff'].includes(auth.user.role)

  const categories = [...new Set(videos.map(v => v.category))].filter(Boolean)
  const videosByCategory = categories.reduce((acc, cat) => {
    acc[cat] = videos.filter(v => v.category === cat).sort((a, b) => a.sort_order - b.sort_order)
    return acc
  }, {} as Record<string, TrainingVideo[]>)

  const uncategorized = videos.filter(v => !v.category).sort((a, b) => a.sort_order - b.sort_order)

  return (
    <AppLayout>
      <Head title="Training" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              Training Center
            </h1>
            <p className="text-chestnut-charcoal/70 mt-1">
              Training modules for agents
            </p>
          </div>
          {isAdmin && (
            <Link href="/training_videos/new" className="btn-primary flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Module
            </Link>
          )}
        </div>

        {categories.map((category) => (
          <div key={category} className="space-y-4">
            <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading border-b border-chestnut-gray pb-2">
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videosByCategory[category].map((video) => (
                <ModuleCard
                  key={video.id}
                  video={video}
                  isAdmin={!!isAdmin}
                />
              ))}
            </div>
          </div>
        ))}

        {uncategorized.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-chestnut-charcoal font-heading border-b border-chestnut-gray pb-2">
              General
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {uncategorized.map((video) => (
                <ModuleCard
                  key={video.id}
                  video={video}
                  isAdmin={!!isAdmin}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

function ModuleCard({ video, isAdmin }: { video: TrainingVideo; isAdmin: boolean }) {
  const Icon = moduleTypeIcons[video.module_type] || Video

  const getButtonText = () => {
    switch (video.module_type) {
      case 'video': return 'Watch Video'
      case 'text': return 'Read Material'
      case 'quiz': return 'Take Quiz'
      default: return 'View'
    }
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-chestnut-forest" />
          <span className="text-xs px-2 py-0.5 rounded-full bg-chestnut-forest/10 text-chestnut-forest">
            {moduleTypeLabels[video.module_type]}
          </span>
          {video.duration_minutes > 0 && (
            <span className="text-xs text-chestnut-charcoal/60 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {video.duration_minutes} min
            </span>
          )}
        </div>
        {video.is_required && (
          <span className="flex items-center gap-1 text-xs text-chestnut-gold font-medium">
            <Star className="h-3 w-3 fill-current" />
            Required
          </span>
        )}
      </div>

      <h3 className="font-semibold text-chestnut-charcoal mb-2">{video.title}</h3>
      <p className="text-sm text-chestnut-charcoal/60 mb-4 line-clamp-2">
        {video.description}
      </p>

      <Link
        href={`/training_videos/${video.id}`}
        className="w-full btn-primary text-center"
      >
        {getButtonText()}
      </Link>

      {isAdmin && (
        <Link
          href={`/training_videos/${video.id}/edit`}
          className="block text-center text-sm text-chestnut-charcoal/60 hover:text-chestnut-forest mt-2"
        >
          Edit
        </Link>
      )}
    </div>
  )
}
